// Grounded ETA + GPU-cost estimates for an upscale bake, so the preflight can
// show a realistic wallclock/$ before committing an expensive set. Numbers are
// MEASURED on the current GPU box (g6e.2xlarge · L40S 48GB) at 4× on a ~37 MP
// output frame — the barrel-crop proof size. Tune here as we gather more data.

// Per-frame seconds at the BASELINE (scale 4, ~37 MP output) by pipeline.
const PERFRAME_BASE_SEC = {
  'supir:supir-only': 2040, // ~34 min — SUPIR does the whole 4× in diffusion (slow, seam-free)
  'supir:two-stage': 600,   // ~10 min — SUPIR 2× recovery + Ultimate-SD 2× finish
  'ultimate-sd': 300,       // ~5 min — diffusion tiled
  'real-esrgan': 60,        // ~1 min — GAN, no diffusion
  swinir: 90,
};
const BASELINE_MP = 37;
// g6e.2xlarge (L40S) on-demand, approx. Override in the UI if the rate changes.
export const GPU_HOURLY_USD = 2.24;
const BOOT_SEC = 60; // cold-start wake of a stopped box (amortized once per drain)

function keyFor(model, mode) {
  if (model === 'supir') return `supir:${mode === 'supir-only' || mode === 'supir' ? 'supir-only' : 'two-stage'}`;
  return model;
}

// Seconds for ONE frame at the given model/mode/scale and output megapixels.
export function perFrameSec({ model = 'supir', mode = 'two-stage', scale = 4, outMp = BASELINE_MP } = {}) {
  const base = PERFRAME_BASE_SEC[keyFor(model, mode)] ?? 600;
  // Diffusion cost tracks output pixels; a lower scale shrinks the frame ~quadratically.
  const scaleFactor = (scale / 4) ** 2;
  const mpFactor = Math.max(0.25, (outMp || BASELINE_MP) / BASELINE_MP);
  return Math.round(base * scaleFactor * mpFactor);
}

// Full bake estimate. Frames run SERIALLY on one GPU, so wallclock ≈ N × perFrame
// (+ one boot). `concurrency` >1 only helps if you run multiple GPU boxes.
export function bakeEstimate({ frames = 1, model = 'supir', mode = 'two-stage', scale = 4, outMp = BASELINE_MP, hourly = GPU_HOURLY_USD, concurrency = 1 } = {}) {
  const per = perFrameSec({ model, mode, scale, outMp });
  const serialSec = per * frames;
  const wallSec = BOOT_SEC + serialSec / Math.max(1, concurrency);
  const gpuSec = BOOT_SEC + serialSec; // billed GPU time is the same regardless of parallel boxes
  const costUsd = (gpuSec / 3600) * hourly;
  return { perFrameSec: per, wallSec, gpuSec, costUsd };
}

// Output megapixels for a crop of the source at a scale (source ≈ 6144×3240 RED).
export function outMpFor({ cropW = 1, cropH = 1, scale = 4, srcW = 6144, srcH = 3240 } = {}) {
  return (srcW * cropW * scale) * (srcH * cropH * scale) / 1e6;
}
